import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CementoCotizacionSavePayload } from './cemento-cotizacion.service';
import { ReferralMetadataService } from './referral-metadata.service';
import {
  MercadoPagoConfirmationConfig,
  MercadoPagoConfirmationStatus,
} from '../models/mercadopago-confirmation';

export interface CementoPaymentInitResponse {
  order_buy: string;
  url: string;
  token?: string;
}

export interface CementoPaymentResult {
  product: string;
  schedule: string;
  hours: string;
  total_price: number;
  request_amount: number;
  address_dispatch: string;
  order_buy: string;
  state: string;
  unitLabel?: string;
}

@Injectable({ providedIn: 'root' })
export class CementoPaymentService {
  private readonly baseUrl = `${environment.apiUrl}cemento/payment/`;
  private readonly mercadoPagoConfirmationBaseUrl = `${environment.apiUrl}mercadopago/`;

  constructor(
    private readonly http: HttpClient,
    private readonly referralMetadataService: ReferralMetadataService,
  ) {}

  initFlow(payload: CementoCotizacionSavePayload): Observable<CementoPaymentInitResponse> {
    return this.http.post<CementoPaymentInitResponse>(`${this.baseUrl}flow/init/`, this.withReferralMetadata(payload));
  }

  initGetnet(payload: CementoCotizacionSavePayload): Observable<CementoPaymentInitResponse> {
    return this.http.post<CementoPaymentInitResponse>(`${this.baseUrl}gnet/init/`, this.withReferralMetadata(payload));
  }

  initMercadoPago(payload: CementoCotizacionSavePayload): Observable<CementoPaymentInitResponse> {
    return this.http.post<CementoPaymentInitResponse>(`${this.baseUrl}mercadopago/init/`, this.withReferralMetadata(payload));
  }

  confirmPayment(
    orderCode: string,
    provider: 'gnet' | 'mercadopago' = 'gnet',
    payload?: Record<string, unknown>,
  ): Observable<{ redirect_url?: string }> {
    return this.http.post<{ redirect_url?: string }>(`${this.baseUrl}${provider}/confirmation/`, {
      order_code: orderCode,
      ...(payload ?? {}),
    });
  }

  cancelPayment(
    orderCode: string,
    provider: 'gnet' | 'mercadopago' = 'gnet',
    payload?: Record<string, unknown>,
  ): Observable<{ redirect_url?: string }> {
    return this.http.post<{ redirect_url?: string }>(`${this.baseUrl}${provider}/cancel/`, {
      order_code: orderCode,
      ...(payload ?? {}),
    });
  }

  getDispatchResult(orderBuy: string): Observable<CementoPaymentResult> {
    return this.http.get<CementoPaymentResult>(`${this.baseUrl}${encodeURIComponent(orderBuy)}/result/`);
  }

  getMercadoPagoConfirmationConfig(resumeToken: string): Observable<MercadoPagoConfirmationConfig> {
    return this.http.get<MercadoPagoConfirmationConfig>(
      `${this.mercadoPagoConfirmationBaseUrl}confirmation-config/${encodeURIComponent(resumeToken)}/`
    );
  }

  getMercadoPagoConfirmationStatus(resumeToken: string): Observable<MercadoPagoConfirmationStatus> {
    return this.http.post<MercadoPagoConfirmationStatus>(
      `${this.mercadoPagoConfirmationBaseUrl}confirmation-status/`,
      { order_code: resumeToken },
    );
  }

  private withReferralMetadata<T extends object>(payload: T): T {
    const metadata = this.referralMetadataService.getMetadata();
    if (!metadata) {
      return payload;
    }

    return {
      ...(payload as Record<string, unknown>),
      referral_metadata: metadata,
    } as T;
  }
}
